import { useEffect, useRef } from 'react';
import { useFocusMode } from './useFocusMode';
import { shouldFocusModeBeActive } from '@/lib/focusModeFilters';
import { useToast } from '@/components/ui/use-toast';

/**
 * Focus Mode Scheduler Hook
 * 
 * Automatically activates and deactivates focus modes based on their schedules.
 * Runs as a background service, checking schedules every minute.
 * 
 * @example
 * ```typescript
 * // In App.tsx or main provider
 * function App() {
 *   useFocusModeScheduler(); // Just call it once
 *   return <YourApp />;
 * }
 * ```
 */
export function useFocusModeScheduler() {
  const { focusModes, activeFocusMode, activate, deactivate } = useFocusMode();
  const { toast } = useToast();
  const lastCheckRef = useRef<Date>(new Date());
  const scheduledActivationRef = useRef<string | null>(null);

  useEffect(() => {
    // Don't run if no modes exist
    if (!focusModes || focusModes.length === 0) {
      return;
    }

    const checkSchedules = () => {
      const now = new Date();
      
      // Prevent duplicate checks within the same minute
      const lastCheck = lastCheckRef.current;
      if (
        lastCheck.getFullYear() === now.getFullYear() &&
        lastCheck.getMonth() === now.getMonth() &&
        lastCheck.getDate() === now.getDate() &&
        lastCheck.getHours() === now.getHours() &&
        lastCheck.getMinutes() === now.getMinutes()
      ) {
        return; // Already checked this minute
      }

      lastCheckRef.current = now;

      // Find modes that should be active based on schedule
      const modesThatShouldBeActive = focusModes.filter(mode => 
        !mode.is_active && // Not already active
        mode.active_times && 
        mode.active_times.length > 0 &&
        shouldFocusModeBeActive(mode, now)
      );

      // Check if we need to activate a scheduled mode
      if (modesThatShouldBeActive.length > 0) {
        // Priority: take the first one (could add priority logic here)
        const modeToActivate = modesThatShouldBeActive[0];
        
        // Only activate if no mode is currently active OR 
        // if the current mode was auto-activated and this one has higher priority
        if (!activeFocusMode || scheduledActivationRef.current === activeFocusMode.id) {
          console.log('[FocusModeScheduler] Auto-activating:', modeToActivate.name);
          
          activate(modeToActivate.id)
            .then(() => {
              scheduledActivationRef.current = modeToActivate.id;
              
              toast({
                title: `${modeToActivate.name} Activated`,
                description: 'Your focus mode has been activated automatically based on your schedule.',
                duration: 4000,
              });
            })
            .catch(error => {
              console.error('[FocusModeScheduler] Failed to auto-activate:', error);
            });
        }
      }

      // Check if the currently active mode should be deactivated
      if (activeFocusMode && scheduledActivationRef.current === activeFocusMode.id) {
        const shouldStillBeActive = shouldFocusModeBeActive(activeFocusMode, now);
        
        if (!shouldStillBeActive) {
          console.log('[FocusModeScheduler] Auto-deactivating:', activeFocusMode.name);
          
          deactivate()
            .then(() => {
              scheduledActivationRef.current = null;
              
              toast({
                title: `${activeFocusMode.name} Deactivated`,
                description: 'Your scheduled focus mode has ended.',
                duration: 4000,
              });
            })
            .catch(error => {
              console.error('[FocusModeScheduler] Failed to auto-deactivate:', error);
            });
        }
      }
    };

    // Run immediately on mount
    checkSchedules();

    // Check every 30 seconds (more responsive than 60 seconds)
    const interval = setInterval(checkSchedules, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [focusModes, activeFocusMode, activate, deactivate, toast]);

  // Reset scheduled activation tracking when user manually changes modes
  useEffect(() => {
    if (activeFocusMode) {
      // Check if this was a manual activation (not in our ref)
      if (scheduledActivationRef.current !== activeFocusMode.id) {
        // User manually activated a mode - clear scheduled tracking
        scheduledActivationRef.current = null;
      }
    }
  }, [activeFocusMode]);
}

/**
 * Hook variant that allows manual control over the scheduler
 * Useful for testing or conditional enabling
 */
export function useControlledFocusModeScheduler(enabled: boolean = true) {
  const { focusModes, activeFocusMode, activate, deactivate } = useFocusMode();
  const { toast } = useToast();
  const lastCheckRef = useRef<Date>(new Date());
  const scheduledActivationRef = useRef<string | null>(null);

  useEffect(() => {
    if (!enabled) return;

    // Same logic as above but wrapped in enabled check
    // (Implementation identical to useFocusModeScheduler)
    
    const checkSchedules = () => {
      const now = new Date();
      
      const lastCheck = lastCheckRef.current;
      if (
        lastCheck.getFullYear() === now.getFullYear() &&
        lastCheck.getMonth() === now.getMonth() &&
        lastCheck.getDate() === now.getDate() &&
        lastCheck.getHours() === now.getHours() &&
        lastCheck.getMinutes() === now.getMinutes()
      ) {
        return;
      }

      lastCheckRef.current = now;

      if (!focusModes || focusModes.length === 0) return;

      const modesThatShouldBeActive = focusModes.filter(mode => 
        !mode.is_active &&
        mode.active_times && 
        mode.active_times.length > 0 &&
        shouldFocusModeBeActive(mode, now)
      );

      if (modesThatShouldBeActive.length > 0) {
        const modeToActivate = modesThatShouldBeActive[0];
        
        if (!activeFocusMode || scheduledActivationRef.current === activeFocusMode.id) {
          activate(modeToActivate.id)
            .then(() => {
              scheduledActivationRef.current = modeToActivate.id;
              toast({
                title: `${modeToActivate.name} Activated`,
                description: 'Your focus mode has been activated automatically.',
                duration: 4000,
              });
            })
            .catch(error => {
              console.error('[FocusModeScheduler] Failed to auto-activate:', error);
            });
        }
      }

      if (activeFocusMode && scheduledActivationRef.current === activeFocusMode.id) {
        const shouldStillBeActive = shouldFocusModeBeActive(activeFocusMode, now);
        
        if (!shouldStillBeActive) {
          deactivate()
            .then(() => {
              scheduledActivationRef.current = null;
              toast({
                title: `${activeFocusMode.name} Deactivated`,
                description: 'Your scheduled focus mode has ended.',
                duration: 4000,
              });
            })
            .catch(error => {
              console.error('[FocusModeScheduler] Failed to auto-deactivate:', error);
            });
        }
      }
    };

    checkSchedules();
    const interval = setInterval(checkSchedules, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [enabled, focusModes, activeFocusMode, activate, deactivate, toast]);
}
